import { useState, useCallback } from "react";
import PageShell from "../../../components/PageShell";
import DataTable from "../../../components/DataTable";
import DataForm from "../../../components/DataForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import ConfirmDialog from "../../../components/ConfirmDialog";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "../../../components/ui/context-menu";
import {
  useEmployees,
  useUpdateEmployee,
  useCreateEmployee,
  useArchiveEmployee,
} from "../hooks/useEmployees";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n";

const COLUMNS = [
  { header: "col.id", accessor: "id", type: "number" },
  { header: "col.firstName", accessor: "firstname", type: "text" },
  { header: "col.lastName", accessor: "lastname", type: "text" },
  { header: "col.username", accessor: "username", type: "text" },
  { header: "col.telegram", accessor: "telegramUsername", type: "text" },
  { header: "col.phone", accessor: "phone", type: "phone" },
  { header: "col.isActive", accessor: "isActive", type: "boolean" },
];

const FORM_FIELDS = [
  { name: "id", label: "col.id", type: "number", disabled: true },
  { name: "firstname", label: "form.firstName", type: "text" },
  { name: "lastname", label: "form.lastName", type: "text" },
  { name: "username", label: "form.username", type: "text" },
  { name: "telegramUsername", label: "form.telegram", type: "text" },
  { name: "phone", label: "form.phone", type: "phone" },
  { name: "isActive", label: "form.active", type: "boolean", required: false },
];

const CREATE_DEFAULTS = {
  firstname: "",
  lastname: "",
  telegramUsername: "",
  phone: "",
};

export default function EmployeesPage() {
  const { t } = useTranslation();
  const { data: employees, isLoading } = useEmployees();
  const updateMutation = useUpdateEmployee();
  const createMutation = useCreateEmployee();
  const archiveMutation = useArchiveEmployee();
  const [editingRow, setEditingRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [values, setValues] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const openEdit = useCallback((row) => {
    setEditingRow(row);
    setValues({ ...row });
    setIsNew(false);
  }, []);

  const openCreate = useCallback(() => {
    setEditingRow(CREATE_DEFAULTS);
    setValues({ ...CREATE_DEFAULTS });
    setIsNew(true);
  }, []);

  const handleSave = () => {
    if (!values) return;
    const mutation = isNew ? createMutation : updateMutation;
    mutation.mutate(values, {
      onSuccess: () => {
        toast.success(
          isNew ? t("toast.recordCreated") : t("toast.recordUpdated"),
        );
        setEditingRow(null);
        setValues(null);
      },
      onError: () =>
        toast.error(
          isNew ? t("toast.recordCreateFailed") : t("toast.recordUpdateFailed"),
        ),
    });
  };

  const renderArchiveMenu = useCallback(
    (row, children) => (
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            className="bg-c-red-500 hover:bg-c-red-600 text-white"
            onClick={() => setArchiveTarget(row)}
          >
            {t("common.archive")}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    ),
    [],
  );

  const handleArchive = () => {
    if (!archiveTarget) return;
    archiveMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        toast.success(t("toast.employeeArchived"));
        setArchiveTarget(null);
      },
      onError: () => toast.error(t("toast.employeeArchiveFailed")),
    });
  };

  return (
    <PageShell
      title={t("page.employees")}
      actions={<Button onClick={openCreate}>{t("common.add")}</Button>}
    >
      {isLoading ? (
        <p className="text-c-gray-400">{t("common.loading")}</p>
      ) : (
        <DataTable
          columns={COLUMNS}
          data={employees}
          onRowClick={openEdit}
          renderContextMenu={renderArchiveMenu}
        />
      )}

      <Dialog
        open={!!editingRow}
        onOpenChange={(o) => {
          if (!o) {
            setEditingRow(null);
            setValues(null);
          }
        }}
      >
        <DialogContent className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-h-none rounded-none sm:rounded-none border-r-0 border-t-0 border-b-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right min-w-[480px] grid-rows-[auto_1fr_auto]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? t("dialog.addEmployee") : t("dialog.editRecord")}
            </DialogTitle>
          </DialogHeader>
          <form
            id="edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto px-2"
          >
            <DataForm
              fields={FORM_FIELDS}
              values={values}
              onChange={setValues}
            />
          </form>
          <DialogFooter className="flex gap-2">
            <Button type="submit" form="edit-form">
              {t("common.save")}
            </Button>
            {!isNew && (
              <Button
                variant="destructive"
                onClick={() => setArchiveTarget(editingRow)}
              >
                {t("common.archive")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(o) => {
          if (!o) setArchiveTarget(null);
        }}
        itemName={`${t("page.employees").toLowerCase()} "${archiveTarget?.firstname}"`}
        onConfirm={handleArchive}
      />
    </PageShell>
  );
}
